import { Box, Button, Card, Divider, Tooltip } from '@mui/material';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Query } from 'src/components/atoms/query';

import { PageContainer } from '../../../../components/molecules/page-container';
import { PageHeader } from '../../../../components/molecules/page-header';
import { Plus } from '../../../../icons/plus';
import { billingGroupsKeys } from '../../api/query-keys/billing-groups';
import { BillingGroupsTable } from '../organisms/billing-groups-table';

import { meiliApi } from '@/api/organization-admin/meili';
import { Pagination } from '@/components/organisms/pagination';
import { useMeilisearchWithUrlParams } from '@/hooks/meilisearch/use-meilisearch-with-url-params';
import { Can, withPermissions } from '@/permissions/can';

const BillingGroups = () => {
  const { t } = useTranslation(['billingGroups', 'common']);

  const navigate = useNavigate({ from: '/billing-groups/' });

  const navigateToCreateGroupForm = () => {
    navigate({ to: '/billing-groups/create' });
  };

  const { page, perPage, query } = useSearch({
    from: '/_layout/billing-groups/',
    select(state) {
      return {
        page: state.page,
        perPage: state.limit,
        query: state.q,
      };
    },
    structuralSharing: true,
  });

  const {
    results,
    isLoading,
    isFetching,
    error,
    handleChangePage,
    handleChangeLimit,
    handleChangeQuery,
  } = useMeilisearchWithUrlParams(
    meiliApi.fetchBillingGroups,
    billingGroupsKeys.list(page, perPage),
  );

  const totalPages = Math.ceil((results?.estimatedTotalHits ?? 0) / perPage);

  return (
    <PageContainer helmetPageTitle={t('billingGroups:tab')}>
      <PageHeader
        title={t('billingGroups:title')}
        extras={
          <Can I="create" a="BillingGroups" passThrough>
            {allowed => (
              <Tooltip
                title={t('common:notSufficientPermissions')}
                disableHoverListener={allowed}
                disableFocusListener={allowed}>
                <span>
                  <Button
                    color="primary"
                    onClick={navigateToCreateGroupForm}
                    variant="contained"
                    size="large"
                    disabled={!allowed}
                    startIcon={<Plus fontSize="small" />}>
                    {t('billingGroups:create.title')}
                  </Button>
                </span>
              </Tooltip>
            )}
          </Can>
        }
      />
      <Card
        variant="outlined"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
        }}>
        <Box sx={{ p: 3 }}>
          <Query
            disabled={false}
            onChange={handleChangeQuery}
            value={query}
            sx={{ py: 1.5 }}
            searchAsType
          />
        </Box>
        <BillingGroupsTable
          data={results?.hits ?? []}
          isLoading={isLoading}
          error={error?.response?.data?.message || ''}
        />
        <Divider sx={{ mt: 'auto' }} />
        <Pagination
          changePage={handleChangePage}
          changePerPage={handleChangeLimit}
          page={page}
          perPage={perPage}
          totalPages={totalPages}
          isFetching={isFetching}
        />
      </Card>
    </PageContainer>
  );
};

export default withPermissions(
  permissions => permissions.cannot('read', 'BillingGroups'),
  BillingGroups,
);
